import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, Directive, effect, inject, input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Tabs } from '@openng/optimus-ui/tabs';
import { TabsPhoneSelectDirective } from './tabs-phone-select.directive';

@Directive({
  selector: 'p-tabs[libTabsQuery]',
  hostDirectives: [TabsPhoneSelectDirective]
})
export class TabsQueryDirective implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly tabsComponent = inject(Tabs);

  // Written as a bare attribute the input arrives as an empty string, hence the fallback below.
  readonly libTabsQuery = input('t');
  readonly libTabsQueryIds = input<(string | undefined)[]>([]);

  static toIndex(value: string | undefined, ids: (string | undefined)[]): number | undefined {
    let index: number | undefined = undefined;
    if (value != undefined) {
      const idIndex = ids.indexOf(value);
      if (+value >= 0) {
        index = +value;
      } else if (idIndex >= 0) {
        index = idIndex;
      }
    }
    return index;
  }

  constructor() {
    effect(() => {
      const index = this.tabsComponent?.value();
      if (index != undefined && +index >= 0) {
        this.navigate(+index);
      }
    });
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const index = TabsQueryDirective.toIndex(
        this.activatedRoute.snapshot.queryParams[this.queryParam()],
        this.libTabsQueryIds()
      );
      this.select(index ?? 0);
    });

    // Only a parameter that carries a value counts: another control may replace the whole query
    // string without this one, and that must not pull the user out of the tab he opened.
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const index = TabsQueryDirective.toIndex(params[this.queryParam()], this.libTabsQueryIds());
      if (index != undefined) {
        this.select(index);
      }
    });
  }

  private select(index: number): void {
    const tabIndex = this.tabsComponent?.value();
    if (tabIndex == undefined || +tabIndex != index) {
      this.tabsComponent?.value.set(index);
    }
  }

  private queryParam(): string {
    return this.libTabsQuery() || 't';
  }

  navigate(index: number): void {
    void this.router.navigate([], {
      queryParams: { [this.queryParam()]: index },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
